/* ============================================================
   Image Card Carousel — assets/image-carousel.js
   ============================================================ */

class ImageCarousel {
  constructor(el) {
    this.el        = el;
    this.viewport  = el.querySelector('.m-ic__viewport');
    this.track     = el.querySelector('.m-ic__track');
    this.cards     = Array.from(el.querySelectorAll('.m-ic__card'));
    this.dotsCt    = el.querySelector('.m-ic__dots');
    this.prevBtn   = el.querySelector('.m-ic__arrow--prev');
    this.nextBtn   = el.querySelector('.m-ic__arrow--next');
    this.nav       = el.querySelector('.m-ic__nav');

    this.currentPage       = 0;
    this._resizeTimer      = null;
    this.autoAdvance       = el.dataset.autoAdvance === 'true';
    this.autoAdvanceSpeed  = (parseInt(el.dataset.autoAdvanceSpeed, 10) || 4) * 1000;
    this._autoTimer        = null;

    // Store instance reference for design-mode access
    el._imageCarousel = this;

    if (!this.track || this.cards.length === 0) return;

    this._init();
    this._bindEvents();
  }

  // ── Responsive: cards visible per page ──────────────────────

  _perPage() {
    if (window.innerWidth <= 520) return 1;
    if (window.innerWidth <= 900) return 2;
    return 4;
  }

  _totalPages() {
    return Math.ceil(this.cards.length / this._perPage());
  }

  // ── Core slide logic ─────────────────────────────────────────

  _getGap() {
    // column-gap from CSS (set via CSS custom property)
    const style = getComputedStyle(this.track);
    return parseFloat(style.columnGap || style.gap) || 0;
  }

  _slide(animate) {
    const perPage    = this._perPage();
    const totalPages = this._totalPages();

    // Clamp page
    this.currentPage = Math.max(0, Math.min(this.currentPage, totalPages - 1));

    // First visible card index (clamp so last page fills completely)
    const rawIndex  = this.currentPage * perPage;
    const maxIndex  = Math.max(0, this.cards.length - perPage);
    const cardIndex = Math.min(rawIndex, maxIndex);

    // Pixel offset
    const cardWidth = this.cards[0].offsetWidth;
    const gap       = this._getGap();
    const offset    = cardIndex * (cardWidth + gap);

    // Apply transform
    if (animate === false) {
      this.track.style.transition = 'none';
      this.track.style.transform  = `translateX(-${offset}px)`;
      // Re-enable transition on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.track.style.transition = '';
        });
      });
    } else {
      this.track.style.transform = `translateX(-${offset}px)`;
    }

    // Update dots
    this._updateDots();

    // Update arrow disabled state
    this._updateArrows();
  }

  // ── Navigation ───────────────────────────────────────────────

  next() {
    this.currentPage = (this.currentPage + 1) % this._totalPages();
    this._slide();
  }

  prev() {
    const total      = this._totalPages();
    this.currentPage = (this.currentPage - 1 + total) % total;
    this._slide();
  }

  goTo(page) {
    this.currentPage = page;
    this._slide();
  }

  // ── Dots ─────────────────────────────────────────────────────

  _renderDots() {
    if (!this.dotsCt) return;
    const total = this._totalPages();
    this.dotsCt.innerHTML = '';

    for (let i = 0; i < total; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'm-ic__dot' + (i === this.currentPage ? ' is-active' : '');
      btn.setAttribute('aria-label', `Go to page ${i + 1}`);
      btn.dataset.page = i;
      this.dotsCt.appendChild(btn);
    }

    // Hide entire nav row if only 1 page
    if (this.nav) {
      this.nav.style.visibility = total <= 1 ? 'hidden' : 'visible';
    }
  }

  _updateDots() {
    if (!this.dotsCt) return;
    this.dotsCt.querySelectorAll('.m-ic__dot').forEach((d, i) => {
      d.classList.toggle('is-active', i === this.currentPage);
    });
  }

  // ── Arrows ───────────────────────────────────────────────────

  _updateArrows() {
    // We use looping so arrows never really disabled, but keep for future
    if (this.prevBtn) this.prevBtn.classList.toggle('m-ic__arrow--disabled', false);
    if (this.nextBtn) this.nextBtn.classList.toggle('m-ic__arrow--disabled', false);
  }

  // ── Auto Advance ─────────────────────────────────────────────

  _startAutoAdvance() {
    if (!this.autoAdvance) return;
    this._stopAutoAdvance();
    this._autoTimer = setInterval(() => this.next(), this.autoAdvanceSpeed);
  }

  _stopAutoAdvance() {
    if (this._autoTimer) {
      clearInterval(this._autoTimer);
      this._autoTimer = null;
    }
  }

  // ── Init ─────────────────────────────────────────────────────

  _init() {
    this._renderDots();
    this._slide(false);
    this._startAutoAdvance();
  }

  // ── Events ───────────────────────────────────────────────────

  _bindEvents() {
    // Prev / Next
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

    // Dot clicks (delegated)
    if (this.dotsCt) {
      this.dotsCt.addEventListener('click', e => {
        const btn = e.target.closest('.m-ic__dot');
        if (!btn) return;
        this.goTo(parseInt(btn.dataset.page, 10));
      });
    }

    // Touch / swipe
    let startX = 0, startY = 0;
    this.el.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      this._stopAutoAdvance();
    }, { passive: true });

    this.el.addEventListener('touchend', e => {
      const dx = startX - e.changedTouches[0].clientX;
      const dy = Math.abs(startY - e.changedTouches[0].clientY);
      if (Math.abs(dx) > 50 && dy < 40) {
        dx > 0 ? this.next() : this.prev();
      }
      this._startAutoAdvance();
    }, { passive: true });

    // Pause auto-advance on hover / focus
    if (this.autoAdvance) {
      this.el.addEventListener('mouseenter', () => this._stopAutoAdvance());
      this.el.addEventListener('mouseleave', () => this._startAutoAdvance());
      this.el.addEventListener('focusin',    () => this._stopAutoAdvance());
      this.el.addEventListener('focusout',   () => this._startAutoAdvance());
    }

    // Resize: debounced
    window.addEventListener('resize', () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        // Re-render dots (per-page may change) then re-slide without animation
        this._renderDots();
        this._slide(false);
      }, 150);
    });
  }
}

// ── Boot ──────────────────────────────────────────────────────

function initImageCarousels(root) {
  (root || document).querySelectorAll('.m-image-carousel').forEach(el => {
    if (!el._imageCarousel) new ImageCarousel(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initImageCarousels());
} else {
  initImageCarousels();
}

// ── Shopify design-mode support ───────────────────────────────

if (window.Shopify && Shopify.designMode) {

  document.addEventListener('shopify:section:load', e => {
    initImageCarousels(e.target);
  });

  document.addEventListener('shopify:section:unload', e => {
    const el = e.target.querySelector('.m-image-carousel');
    if (el && el._imageCarousel) {
      el._imageCarousel._stopAutoAdvance();
      el._imageCarousel = null;
    }
  });

  // When a card block is selected in the editor, scroll to that card's page
  document.addEventListener('shopify:block:select', e => {
    const carousel = e.target.closest('.m-image-carousel');
    if (!carousel || !carousel._imageCarousel) return;
    const instance = carousel._imageCarousel;
    const idx      = instance.cards.indexOf(e.target);
    if (idx < 0) return;
    const page = Math.floor(idx / instance._perPage());
    instance.goTo(page);
  });
}
